import { useEffect, useState } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { useTheme } from "next-themes";
import { toast } from "sonner";
import {
  StoreIcon,
  ChevronsUpDownIcon,
  LogOutIcon,
  CircleUserIcon,
  MonitorIcon,
  MoonIcon,
  SunIcon,
} from "lucide-react";
import { useSession, signOut } from "@/lib/auth-client";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarTrigger,
} from "@/components/ui/sidebar";

const NAV = [{ to: "/", label: "Stores", icon: StoreIcon }] as const;

export function AppSidebar() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const { data: session } = useSession();

  return (
    <Sidebar collapsible="icon">
      <SidebarHeader className="h-14 justify-center px-2">
        <div className="flex items-center justify-between gap-2 group-data-[collapsible=icon]:justify-center">
          <Link
            to="/"
            className="focus-ring truncate rounded-lg px-2 text-sm font-semibold tracking-tight group-data-[collapsible=icon]:hidden"
          >
            BibaCV
          </Link>
          <SidebarTrigger className="size-8 rounded-lg" />
        </div>
      </SidebarHeader>

      <SidebarContent className="px-1.5">
        <SidebarGroup className="py-1">
          <SidebarGroupContent>
            <SidebarMenu>
              {NAV.map((item) => (
                <SidebarMenuItem key={item.to}>
                  <SidebarMenuButton
                    asChild
                    tooltip={item.label}
                    isActive={pathname === item.to}
                    className="h-9 rounded-lg"
                  >
                    <Link to={item.to}>
                      <item.icon />
                      <span>{item.label}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-1.5">
        <SidebarMenu>
          <SidebarMenuItem>
            <UserMenu
              id={session?.user.id ?? null}
              email={session?.user.email ?? null}
              name={session?.user.name ?? null}
            />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

function UserMenu({
  id,
  email,
  name,
}: {
  id: string | null;
  email: string | null;
  name: string | null;
}) {
  if (!id || !email) {
    return (
      <SidebarMenuButton className="h-11 rounded-lg" tooltip="Not signed in" disabled>
        <CircleUserIcon />
        <span className="text-muted-foreground">Not signed in</span>
      </SidebarMenuButton>
    );
  }

  const initials = (name ?? email)
    .split(/[\s@.]+/u)
    .filter(Boolean)
    .map((part) => part[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <SidebarMenuButton className="h-11 rounded-lg" tooltip={email}>
          <Avatar className="size-6 rounded-md">
            <AvatarFallback className="rounded-md bg-accent text-[10px] font-medium">
              {initials || "?"}
            </AvatarFallback>
          </Avatar>
          <span className="truncate text-sm">{name || email}</span>
          <ChevronsUpDownIcon className="ml-auto opacity-50" />
        </SidebarMenuButton>
      </DropdownMenuTrigger>
      <DropdownMenuContent side="top" align="start" className="w-56">
        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
          {email}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/users/$userId" params={{ userId: id }}>
            <CircleUserIcon />
            Profile
          </Link>
        </DropdownMenuItem>
        <ThemeItem />
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => void signOut().then(() => toast.success("Signed out"))}
          variant="destructive"
        >
          <LogOutIcon />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const THEME_CYCLE = { system: "light", light: "dark", dark: "system" } as const;
type ThemeName = keyof typeof THEME_CYCLE;

function ThemeItem() {
  const { theme, setTheme } = useTheme();
  // Theme is client-only state; rendering it during hydration would mismatch.
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const current: ThemeName =
    mounted && theme && theme in THEME_CYCLE ? (theme as ThemeName) : "system";
  const Icon = current === "light" ? SunIcon : current === "dark" ? MoonIcon : MonitorIcon;

  return (
    <DropdownMenuItem
      onSelect={(event) => {
        event.preventDefault();
        setTheme(THEME_CYCLE[current]);
      }}
    >
      <Icon />
      Theme: {mounted ? current : "system"}
    </DropdownMenuItem>
  );
}
